/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 블록컨테이너(자식 블록을 담는 컨테이너) 엘리먼트를 정의한다.
 *
 * @file /src/rootin/elements/container/Container.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 9. 9.
 */
import * as parent from '@rootin/base/Element';
import { Block } from '@rootin/base/Block';
import { Dom } from '@utils/Dom';
import { Html } from '@utils/Html';

export interface Properties extends parent.Properties {
    /**
     * 이 영역에 삽입 가능한 블록타입 목록
     */
    blocks: string[];

    /**
     * 에디터가 현재 컨테이너를 블록 삽입 대상으로 사용할지 여부
     */
    insertTarget?: boolean;

    /**
     * 비었을 때 기본 블록을 자동으로 채울지 여부 (기본값 true)
     */
    autoSeed?: boolean;

    /**
     * 기본블록을 반환하는 헬퍼
     */
    defaultBlock?: (container: Container) => Block;

    /**
     * 블록추가버튼 DOM 을 직접 구성하는 헬퍼. NULL 을 반환하면 기본 DOM 구조를 사용한다.
     */
    createMarker?: (container: Container, isLast: boolean) => Dom | null;

    /**
     * 블록추가버튼 핸들러
     */
    addHandler?: (container: Container, $marker: Dom) => Promise<void>;
}

export class Container extends parent.Element {
    /**
     * 엘리먼트타입
     */
    type: string = 'container';

    /**
     * 엘리먼트종류
     */
    kind: 'text' | 'atomic' | 'container' = 'container';

    /**
     * 컨테이너에 허용되는 블록목록
     */
    blocks: string[];

    /**
     * 에디터가 현재 컨테이너를 블록 삽입 대상으로 사용할지 여부
     */
    insertTarget: boolean;

    /**
     * 비었을 때 기본 블록을 자동으로 채울지 여부
     */
    autoSeed: boolean;

    /**
     * 기본블록을 반환하는 헬퍼
     */
    defaultBlock: (container: Container) => Block | null;

    /**
     * 블록추가버튼 DOM 을 직접 구성하는 헬퍼
     */
    createMarker: (container: Container, isLast: boolean) => Dom | null;

    /**
     * 블록추가버튼 핸들러
     */
    addHandler: (container: Container, $marker: Dom) => Promise<void> | null;

    /**
     * 중첩된 onBeforeEdit~onEdit 호출 깊이
     */
    #pendingEditDepth: number = 0;

    /**
     * 가장 바깥 onBeforeEdit 시점의 히스토리 스택 길이
     */
    #editHistoryIndex: number = 0;

    /**
     * 블록컨테이너(자식 블록을 담는 컨테이너) 엘리먼트를 정의한다.
     *
     * @param block - 이 엘리먼트가 속한 블록
     * @param properties - 엘리먼트설정
     */
    constructor(
        block: Block,
        public properties: Properties
    ) {
        super(block, properties);
        this.blocks = properties.blocks;
        this.insertTarget = properties.insertTarget ?? true;
        this.autoSeed = properties.autoSeed ?? true;
        this.defaultBlock = properties.defaultBlock ?? null;
        this.createMarker = properties.createMarker ?? null;
        this.addHandler = properties.addHandler ?? null;
    }

    /**
     * 이 영역의 직속 자식 블록들을 문서 순서대로 가져온다.
     *
     * @returns 블록 목록
     */
    getBlocks(): Block[] {
        return this.#getBlocks()
            .map(($block) => $block.getData('block') as Block | null)
            .filter((block): block is Block => block !== null);
    }

    /**
     * 구조변경 직전 자기 자신의 현재 값을 히스토리에 기록한다.
     */
    onBeforeEdit(): void {
        super.onBeforeEdit();

        if (this.#pendingEditDepth === 0) {
            this.#editHistoryIndex = this.getEditor().history.stack.length;
            this.getEditor().history.snapshots.set(this.getId(), this.getValue());
        }

        this.#pendingEditDepth++;
    }

    /**
     * 구조변경 이후 히스토리를 기록한다.
     */
    onEdit(): void {
        if (this.#pendingEditDepth === 0) {
            super.onEdit();
            return;
        }

        this.#pendingEditDepth--;
        if (this.#pendingEditDepth > 0) {
            return;
        }

        this.getEditor().history.stack.splice(this.#editHistoryIndex);
        super.onEdit();
    }

    /**
     * 이 영역에 블록을 삽입한다.
     *
     * @param block - 삽입할 블록
     * @param before - 삽입할 위치 (없으면 포커스 기준으로 스스로 판단)
     */
    async insertBlock(block: Block, before: Dom | Node | null = null): Promise<void> {
        if (this.blocks.includes(block.getType()) === false) {
            return;
        }

        if (before === null) {
            // 포커스된 블록이 이 컨테이너의 직속 자식이 아닐 수 있다(예: Images 안 Item/Hidden
            // 처럼 몇 단계 더 안쪽) — 조상 방향으로 올라가며 이 컨테이너의 직속 자식을 찾는다.
            let anchor: Block | null = this.getEditor().focus.getBlock();
            while (anchor !== null && anchor.getParent() !== this) {
                anchor = anchor.getParent()?.block ?? null;
            }
            const reference = anchor ?? (this.#getBlocks().at(-1)?.getData('block') as Block) ?? null;

            if (reference !== null && reference.isEmpty() === true && reference.getKind() === 'inline') {
                if (reference.getType() === block.getType()) {
                    return;
                }

                await this.replaceBlock(block, reference);
                return;
            }

            before = reference?.$getBlock().getEl().nextSibling ?? null;
        }

        this.onBeforeEdit();

        this.$getElement().insertBefore(block.$getBlock(), before);
        await block.render();

        await block.onInserted();

        this.onEdit();
    }

    /**
     * 특정 블록을 다른 타입의 새 블록으로 교체한다.
     *
     * @param block - 새 블록
     * @param target - 교체할 블록(없을 경우 현재 포커스된 블록)
     */
    async replaceBlock(block: Block, target: Block | null = null): Promise<void> {
        const old = target ?? this.getEditor().focus.getBlock();
        if (old.getParent() !== this) {
            return;
        }

        this.onBeforeEdit();

        this.$getElement().insertAfter(block.$getBlock(), old.$getBlock());
        old.$getBlock().remove();
        await block.render();

        await block.onInserted();

        this.onEdit();
    }

    /**
     * 특정 블록을 제거한다.
     *
     * @param target - 제거할 블록 (없을 경우 현재 포커스된 블록)
     */
    async removeBlock(target: Block | null = null): Promise<void> {
        const current = target ?? this.getEditor().focus.getBlock();
        if (current.getParent() !== this) {
            return;
        }

        this.onBeforeEdit();

        let $focus = current.$getBlock().prev('[data-block]');
        current.$getBlock().remove();

        if (this.#getBlocks().length === 0 && this.autoSeed === true) {
            await this.#seed();
            $focus = this.#getBlocks()[0] ?? null;
        }

        this.onEdit();

        this.getEditor().focus.setBlock($focus !== null ? ($focus.getData('block') as Block) : null);
    }

    /**
     * 현재 블록을 캐럿 기준으로 분리한다.
     *
     * @param tail - 캐럿 기준 뒷부분을 담을 새 블록
     * @param target - 분리할 블록 (없을 경우 현재 블록)
     * @param element - 캐럿이 있던 엘리먼트
     * @param tailElementName - 뒷부분을 옮겨담을 tail 블록의 엘리먼트명 (없으면 element 와 같은 이름)
     */
    async splitBlock(
        tail: Block,
        target: Block | null = null,
        element: parent.Element | null = null,
        tailElementName: string | null = null
    ): Promise<void> {
        const current = target ?? this.getEditor().focus.getBlock();
        if (current.getParent() !== this) {
            return;
        }

        element ??= this.getEditor().focus.getElement();
        const range = this.getEditor().selection.getRange();
        if (element === null || range === null) {
            return;
        }

        const tailElement = Html.get(`[data-name="${tailElementName ?? element.name}"]`, tail.$getBlock())?.getData(
            'element'
        ) as parent.Element | undefined;
        if (tailElement === undefined) {
            return;
        }

        this.onBeforeEdit();

        if (range.collapsed === false) {
            range.deleteContents();
        }

        const el = element.$getElement().getEl();
        const tailRange = document.createRange();
        tailRange.setStart(range.startContainer, range.startOffset);
        tailRange.setEnd(el, el.childNodes.length);
        const extracted = tailRange.extractContents();

        this.$getElement().insertAfter(tail.$getBlock(), current.$getBlock());
        tailElement.$getElement().getEl().appendChild(extracted);
        await tail.render();

        this.onEdit();

        this.getEditor().caret.toStart(tailElement);
        this.getEditor().focus.setElement(tailElement);
    }

    /**
     * 같은 컨테이너의 인라인 형제 블록 두 개를 하나로 합친다.
     *
     * @param first - 살아남을 블록(앞)
     * @param second - 흡수되어 제거될 블록(뒤)
     */
    async mergeBlock(first: Block, second: Block): Promise<void> {
        if (first.getParent() !== this || second.getParent() !== this) {
            return;
        }

        const name = this.getEditor().focus.getElement()?.name;
        if (name === undefined) {
            return;
        }

        const firstElement = Html.get(`[data-name="${name}"]`, first.$getBlock())?.getData('element') as
            | parent.Element
            | undefined;
        const secondElement = Html.get(`[data-name="${name}"]`, second.$getBlock())?.getData('element') as
            | parent.Element
            | undefined;
        if (firstElement === undefined || secondElement === undefined) {
            return;
        }

        this.onBeforeEdit();

        const firstHtml = firstElement.getValue() as string;
        const caretOffset = firstElement.$getElement().getEl().textContent?.length ?? 0;
        const secondText = secondElement.$getElement().getEl().textContent ?? '';

        second.$getBlock().remove();
        await firstElement.setValue((firstHtml ?? '') + this.#escape(secondText));

        this.onEdit();

        this.getEditor().selection.setOffsets(firstElement, caretOffset, caretOffset);
        this.getEditor().focus.setElement(firstElement);
    }

    /**
     * 컨테이너가 비었을 경우, autoSeed 설정에 따라 기본 블록을 채운다.
     * 이미지 블록처럼 비어있어도 기본 블록을 채우지 않는 컨테이너는 그대로 둔다.
     */
    async seedIfEmpty(): Promise<void> {
        if (this.#getBlocks().length === 0 && this.autoSeed === true) {
            await this.#seed();
        }
    }

    /**
     * 기본 블록 인스턴스를 만든다.
     *
     * @returns 기본 블록 인스턴스 (만들 수 없으면 null)
     */
    createDefaultBlock(): Block | null {
        return this.defaultBlock?.(this) ?? this.block.getBlockClass(this.blocks[0])?.create(this.getEditor()) ?? null;
    }

    /**
     * 엘리먼트를 렌더링한다.
     */
    async render(): Promise<void> {
        if (this.#getBlocks().length > 0 || this.autoSeed === false) {
            return;
        }

        await this.#seed();
    }

    /**
     * 엘리먼트가 비었는지 확인한다.
     *
     * @returns 엘리먼트가 비었는지 여부
     */
    isEmpty(): boolean {
        const blocks = this.#getBlocks();
        return blocks.length === 0 || (blocks.length === 1 && (blocks[0].getData('block') as Block).isEmpty());
    }

    /**
     * 엘리먼트값을 가져온다.
     *
     * @returns 엘리먼트값
     */
    getValue(): { [key: string]: any }[] {
        const output: { [key: string]: any }[] = [];

        for (const $child of this.#getBlocks()) {
            const block = $child.getData('block') as Block;
            output.push(block.getValue());
        }

        return output;
    }

    /**
     * 엘리먼트값을 지정한다.
     *
     * @param value - 엘리먼트값
     */
    async setValue(value: { [key: string]: any }[] | null): Promise<void> {
        const blocks = value ?? (this.block.getValue(this) as { [key: string]: any }[] | null);

        if (blocks === null || blocks.length === 0) {
            this.$getElement().empty();
            if (this.autoSeed === true) {
                await this.#seed();
            }
            return;
        }

        const stale = new Map<string, Dom>();
        for (const $child of this.#getBlocks()) {
            const block = $child.getData('block') as Block;
            stale.set(block.getId(), $child);
        }

        let $previous: Dom | null = null;
        for (const data of blocks) {
            let $current = data.id !== undefined ? (stale.get(data.id) ?? null) : null;

            if ($current !== null) {
                // 안 바뀐(살아있는) 블록 — DOM 은 그대로 두고 위치만 필요하면 옮긴다.
                stale.delete(data.id);
                if ($previous === null) {
                    this.$getElement().prepend($current);
                } else if ($current.prev() !== $previous) {
                    this.$getElement().insertAfter($current, $previous);
                }

                const block = $current.getData('block') as Block;
                await block.setValue(data);
            } else {
                // 새로 생긴 id — 블록을 새로 만들어 그 자리에 끼워넣는다.
                const BlockClass = this.block.getBlockClass(data.type);
                if (BlockClass === null) {
                    continue;
                }

                const block = BlockClass.create(this.getEditor());
                if (data.id !== undefined) {
                    block.setId(data.id);
                }
                $current = block.$getBlock();

                if ($previous === null) {
                    this.$getElement().prepend($current);
                } else {
                    this.$getElement().insertAfter($current, $previous);
                }

                await block.render();
                await block.setValue(data);
            }

            $previous = $current;
        }

        // 새 목록에 더 이상 없는(사라진) id 만 제거한다.
        for (const $stale of stale.values()) {
            $stale.remove();
        }
    }

    /**
     * 컨테이너 HTML 을 받아 내부 블록을 복원한다.
     *
     * @param $containerEl - 이 컨테이너 몫의 마크업(자식으로 `rootin-block`들을 담고 있다)
     */
    async setValueFromHtml($containerEl: globalThis.Element): Promise<void> {
        for (const $child of Array.from($containerEl.children)) {
            if ($child.tagName !== 'ROOTIN-BLOCK') {
                continue;
            }

            const ChildBlockClass = this.block.getBlockClass($child.getAttribute('data-type'));
            if (ChildBlockClass === null) {
                continue;
            }

            const childBlock = ChildBlockClass.create(this.getEditor());
            await childBlock.setValueFromHtml($child.outerHTML);

            this.$getElement().append(childBlock.$getBlock());
            await childBlock.render();
        }
    }

    /**
     * 이 영역의 직속 자식 중 블록만 가져온다.
     *
     * @returns 블록 DOM 목록
     */
    #getBlocks(): Dom[] {
        return Html.all('> rootin-block', this.$getElement()).getList();
    }

    /**
     * 순수 텍스트를 HTML 마크업 문자열 안에 안전하게 넣을 수 있도록 이스케이프한다.
     *
     * @param text - 이스케이프할 순수 텍스트
     * @returns 이스케이프된 문자열
     */
    #escape(text: string): string {
        return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }

    /**
     * 엘리먼트가 비웠을 때 기본 블록을 추가한다.
     * 먼저 이 엘리먼트를 소유한 블록에게 기본 블록을 요청하고, 블록이 기본 블록을 주지 않는다면,
     * 허용 블록 목록의 첫번째 블록을 추가한다.
     */
    async #seed(): Promise<void> {
        const block = this.createDefaultBlock();
        if (block === null) {
            return;
        }

        this.$getElement().append(block.$getBlock());
        await block.render();
    }
}
